// src/pages/projectDetailTabs/ScopeTab.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useData } from "../../DataContext";
import { generateId } from "../../utils/id";
import { apiRequest } from "../../api/http";

import { useTranslation } from "react-i18next";
import { Card } from "../../components/ui/Card";
import { TableShell, CellClamp } from "../../components/ui/TableShell";

type Props = {
  projectId: string;
  onChanged?: () => void | Promise<void>;
};


type ApiScopeItem = {
  id: string;
  tenant_id: string;
  project_id: string;
  job_area: string;
  description: string;
  estimate: number;
  created_at?: string;
  updated_at?: string;
};

type UiScopeItem = {
  id: string;
  projectId: string;
  jobArea: string;
  description: string;
  estimate: number;
};

type ApiOk<T> = { ok: true; data: T };
type ApiErr = { ok: false; error: string };

const money = (n: number) =>
  `$${(Number(n) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

function toUi(row: ApiScopeItem): UiScopeItem {
  return {
    id: row.id,
    projectId: row.project_id,
    jobArea: row.job_area,
    description: row.description,
    estimate: Number(row.estimate) || 0,
  };
}

export default function ScopeTab({ projectId, onChanged }: Props) {
  const { t } = useTranslation();
  const { tenantId, refreshProjectData } = useData() as any;


  const [items, setItems] = useState<UiScopeItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Add form
  const [jobArea, setJobArea] = useState("");
  const [description, setDescription] = useState("");
  const [estimate, setEstimate] = useState("");

  // Inline edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editJobArea, setEditJobArea] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editEstimate, setEditEstimate] = useState("");

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await apiRequest<ApiOk<ApiScopeItem[]> | ApiErr>(
        "GET",
        `/api/scope?project_id=${encodeURIComponent(projectId)}`,
        { tenantId }
      );

      if ((res as any).ok) {
        const rows = ((res as any).data || []) as ApiScopeItem[];
        setItems(rows.map(toUi));
      } else {
        setErr((res as any).error || t("scope.failLoad"));
      }
    } catch (e: any) {
      setErr(e?.message || t("scope.failLoad"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, projectId]);

  const totalEstimate = useMemo(
    () => items.reduce((sum, s) => sum + (Number(s.estimate) || 0), 0),
    [items]
  );

  const handleAdd = async () => {
    if (!jobArea.trim() || !description.trim() || !estimate) {
      alert(t("scope.requiredFieldsAlert"));
      return;
    }

    const est = Number(estimate);
    if (Number.isNaN(est) || est < 0) {
      alert(t("scope.estimateInvalidAlert"));
      return;
    }

    const newId = generateId("SCOP");

    try {
      const res = await apiRequest<ApiOk<ApiScopeItem> | ApiErr>(
        "POST",
        "/api/scope",
        {
          tenantId,
          body: {
            id: newId,
            project_id: projectId,
            job_area: jobArea.trim(),
            description: description.trim(),
            estimate: est,
          },
        }
      );

      if (!(res as any).ok) {
        alert((res as any).error || t("scope.failAdd"));
        return;
      }

      const created = toUi((res as any).data as ApiScopeItem);
      setItems((prev) => [created, ...prev]);
      await refreshProjectData(projectId);

      setJobArea("");
      setDescription("");
      setEstimate("");
    } catch (e: any) {
      alert(e?.message || t("scope.failAdd"));
    }
  };

  const startEdit = (s: UiScopeItem) => {
    setEditingId(s.id);
    setEditJobArea(s.jobArea || "");
    setEditDescription(s.description || "");
    setEditEstimate(String(s.estimate ?? ""));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditJobArea("");
    setEditDescription("");
    setEditEstimate("");
  };

  const saveEdit = async (s: UiScopeItem) => {
    if (!editJobArea.trim() || !editDescription.trim() || !editEstimate) {
      alert(t("scope.requiredFieldsAlert"));
      return;
    }

    const est = Number(editEstimate);
    if (Number.isNaN(est) || est < 0) {
      alert(t("scope.estimateInvalidAlert"));
      return;
    }

    try {
      const res = await apiRequest<ApiOk<ApiScopeItem> | ApiErr>(
        "PATCH",
        `/api/scope/${s.id}`,
        {
          tenantId,
          body: {
            job_area: editJobArea.trim(),
            description: editDescription.trim(),
            estimate: est,
          },
        }
      );

      if (!(res as any).ok) {
        alert((res as any).error || t("scope.failUpdate"));
        return;
      }

      const updated = toUi((res as any).data as ApiScopeItem);
      setItems((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
      await refreshProjectData(projectId);
      cancelEdit();
    } catch (e: any) {
      alert(e?.message || t("scope.failUpdate"));
    }
  };

  const doDelete = async (s: UiScopeItem) => {
    const ok = window.confirm(
      `Delete this scope item?\n\n${s.jobArea}: ${s.description}\n${money(s.estimate)}`
    );
    if (!ok) return;

    try {
      const res = await apiRequest<ApiOk<any> | ApiErr>(
        "DELETE",
        `/api/scope/${s.id}`,
        { tenantId }
      );

      if (!(res as any).ok) {
        alert((res as any).error || t("scope.failDelete"));
        return;
      }

      setItems((prev) => prev.filter((x) => x.id !== s.id));
      await refreshProjectData(projectId);
      if (editingId === s.id) cancelEdit();
    } catch (e: any) {
      alert(e?.message || t("scope.failDelete"));
    }
  };

  return (
    <div className="container" style={{ padding: 0 }}>
      <style>{`
        .scopeGrid {
          display: grid;
          gap: 12px;
          align-items: end;
          grid-template-columns:
            minmax(220px, 1.1fr)
            minmax(320px, 2fr)
            minmax(220px, 0.9fr);
        }
        .scopeCell { display: flex; flex-direction: column; min-width: 0; }
        @media (max-width: 980px) { .scopeGrid { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 680px) { .scopeGrid { grid-template-columns: 1fr; } }

        .fieldLabel { color: var(--muted); font-weight: 900; font-size: 12px; margin-bottom: 6px; }
        .fieldInput {
          width: 100%;
          min-width: 0;
          max-width: 100%;
          padding: 10px 12px;
          border-radius: 12px;
          border: 1px solid rgba(15,23,42,0.12);
          background: rgba(255,255,255,0.85);
          outline: none;
          box-sizing: border-box;
        }
        .fieldInputNum { padding-right: 14px; }
        .fieldInput:focus { border-color: rgba(15,23,42,0.22); box-shadow: 0 0 0 3px rgba(15,23,42,0.08); }

        .scopeHeaderRight { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; justify-content: flex-end; }
        .scopePillBtn { border-radius: 999px; padding: 8px 12px; font-weight: 900; line-height: 1; white-space: nowrap; }
        .scopeHeaderTotal { display: inline-flex; align-items: center; gap: 8px; }

        .scopeEmpty { color: var(--muted); font-style: italic; padding: 10px 2px; }
        .rowActions { display: flex; gap: 8px; justify-content: flex-end; flex-wrap: wrap; }
        .numRight { text-align: right; }
      `}</style>

      <Card
        title={
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div style={{ fontWeight: 1000, letterSpacing: "-0.2px" }}>{t("scope.title")}</div>

            <div className="scopeHeaderRight">
              <div className="badge scopeHeaderTotal" title={t("scope.totalTip")}>
                <span>{t("common.total")}:</span>
                <span style={{ fontWeight: 1000 }}>{money(totalEstimate)}</span>
              </div>

              <button className="btn btnSm scopePillBtn" onClick={handleAdd} type="button">
                {t("scope.addBtn")}
              </button>
            </div>
          </div>
        }
        subtitle={t("scope.subtitle")}
      >
        {err ? (
          <div style={{ color: "var(--bad)", fontWeight: 800, marginBottom: 10 }}>{err}</div>
        ) : null}

        <div className="scopeGrid">
          <div className="scopeCell">
            <div className="fieldLabel">{t("scope.jobArea")}</div>
            <input
              className="fieldInput"
              placeholder={t("scope.jobAreaPlaceholder")}
              value={jobArea}
              onChange={(e) => setJobArea(e.target.value)}
            />
          </div>

          <div className="scopeCell">
            <div className="fieldLabel">{t("scope.description")}</div>
            <input
              className="fieldInput"
              placeholder={t("scope.descriptionPlaceholder")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="scopeCell">
            <div className="fieldLabel">{t("scope.estimate")}</div>
            <input
              className="fieldInput fieldInputNum"
              type="number"
              inputMode="decimal"
              placeholder="0.00"
              value={estimate}
              onChange={(e) => setEstimate(e.target.value)}
            />
          </div>
        </div>

        <div className="noteOnCard" style={{ marginTop: 10 }}>
          {t("scope.tipNote")}
        </div>
      </Card>

      <div style={{ marginTop: 14 }}>
        <TableShell
          title={t("scope.listTitle")}
          subtitle={
            loading
              ? "Loading..."
              : items.length
              ? t("scope.itemCount", { count: items.length })
              : t("scope.noItemsYet")
          }
          right={
            <span className="badge">
              {t("common.total")}: <b>{money(totalEstimate)}</b>
            </span>
          }
        >
          {items.length === 0 ? (
            <div className="scopeEmpty">{t("scope.noItemsYetMsg")}</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th style={{ width: 180 }}>{t("scope.jobArea")}</th>
                  <th>{t("scope.description")}</th>
                  <th style={{ width: 160 }} className="numRight">
                    {t("scope.colEstimate")}
                  </th>
                  <th style={{ width: 170 }} />
                </tr>
              </thead>

              <tbody>
                {items.map((s) => {
                  const isEditing = editingId === s.id;

                  return (
                    <tr key={s.id}>
                      <td>
                        {isEditing ? (
                          <input
                            className="fieldInput"
                            value={editJobArea}
                            onChange={(e) => setEditJobArea(e.target.value)}
                          />
                        ) : (
                          <CellClamp lines={2} title={s.jobArea || ""}>
                            <span style={{ fontWeight: 900 }}>{s.jobArea}</span>
                          </CellClamp>
                        )}
                      </td>

                      <td>
                        {isEditing ? (
                          <input
                            className="fieldInput"
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                          />
                        ) : (
                          <div
                            style={{
                              whiteSpace: "pre-wrap",
                              overflowWrap: "anywhere",
                              lineHeight: "1.25rem",
                            }}
                          >
                            {s.description}
                          </div>
                        )}
                      </td>

                      <td className="numRight" style={{ whiteSpace: "nowrap" }}>
                        {isEditing ? (
                          <input
                            className="fieldInput fieldInputNum"
                            type="number"
                            inputMode="decimal"
                            value={editEstimate}
                            onChange={(e) => setEditEstimate(e.target.value)}
                          />
                        ) : (
                          <span style={{ fontWeight: 900 }}>{money(s.estimate)}</span>
                        )}
                      </td>

                      <td>
                        {isEditing ? (
                          <div className="rowActions">
                            <button className="btn btnSm" onClick={() => saveEdit(s)} type="button">{t("scope.saveBtn")}</button>
                            <button className="btn btnSecondary btnSm" onClick={cancelEdit} type="button">{t("scope.cancelBtn")}</button>
                          </div>
                        ) : (
                          <div className="rowActions">
                            <button className="btn btnSecondary btnSm" onClick={() => startEdit(s)} type="button">{t("scope.editBtn")}</button>
                            <button className="btn btnSecondary btnSm" onClick={() => doDelete(s)} type="button" style={{ color: "#b00020" }}>{t("scope.deleteBtn")}</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}

                <tr>
                  <td colSpan={2} style={{ fontWeight: 1000, textAlign: "right" }}>
                    {t("scope.totalEstimate")}
                  </td>
                  <td className="numRight" style={{ fontWeight: 1000, whiteSpace: "nowrap" }}>
                    {money(totalEstimate)}
                  </td>
                  <td />
                </tr>
              </tbody>
            </table>
          )}
        </TableShell>
      </div>
    </div>
  );
}

